import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2Icon, FileDownIcon, GlobeIcon, ShieldCheckIcon } from 'lucide-react';
import { CtaButton } from '../ui/CtaButton';
import { heroMockupUrl, offer, OFFER_ANCHOR } from '../../data/offer';
const quickFacts = ['26 Weeks', '78 Workouts', '6 Blocks', '5 Bonuses', 'Printable PDF'];
function HeroImage({
  className = ''
}: {
  className?: string;
}) {
  return <img src={heroMockupUrl} alt="HYROX Mega Pack: the 26-week training plan on a desktop screen, surrounded by the five bonus resources" width={1254} height={1254} className={`aspect-square object-contain ${className}`} style={{
    WebkitMaskImage: 'radial-gradient(ellipse 72% 72% at 50% 50%, #000 62%, transparent 100%)',
    maskImage: 'radial-gradient(ellipse 72% 72% at 50% 50%, #000 62%, transparent 100%)'
  }} data-id="element-100" data-mp="mtisrsqczbm6" />;
}
function Pill() {
  return <p className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-gold sm:text-xs sm:tracking-[0.18em]" data-id="element-101" data-mp="1ajuycwy4sw9l">
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-gold" data-id="element-102" data-mp="4qstvn2xlgq9" />
      For HYROX, gym &amp; fitness instructors
    </p>;
}
function Headline({
  className = ''
}: {
  className?: string;
}) {
  return <h1 className={`font-display font-extrabold uppercase leading-[0.92] text-white ${className}`} data-id="element-103" data-mp="3rvs7nkcji8ck">
      Half a year of HYROX classes. <span className="text-gold" data-id="element-104" data-mp="nephmts65g9n">Fully planned.</span>
    </h1>;
}
function Details({
  center = false
}: {
  center?: boolean;
}) {
  return <>
      <p className={`max-w-xl text-lg leading-relaxed text-white/80 ${center ? 'mx-auto' : ''}`} data-id="element-105" data-mp="imefdk9yhidx">
        HYROX MEGA PACK™ is your complete 26-week training plan: 78 fully written sessions with exercises, sets, reps,
        rest periods and progressions, plus five practical bonuses. All done for you.
      </p>
      <ul className={`mt-5 flex flex-wrap gap-x-5 gap-y-2.5 ${center ? 'justify-center' : ''}`} data-id="element-106" data-mp="16c8phu2w4suh">
        {quickFacts.map(f => <li key={f} className="flex items-center gap-1.5 text-base font-semibold text-white" data-id="element-107" data-mp="35la0gupz9hza">
            <CheckCircle2Icon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-108" data-mp="2qwnjkokpci5y" />
            {f}
          </li>)}
      </ul>
    </>;
}
function TrustRow({
  center = false
}: {
  center?: boolean;
}) {
  return <ul className={`flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-white/65 ${center ? 'justify-center' : ''}`} data-id="element-109" data-mp="2hueb048zuyv7">
      <li className="flex items-center gap-1.5" data-id="element-110" data-mp="2msfugqac1077">
        <FileDownIcon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-111" data-mp="27y1l7hgkl55q" />
        Digital PDF
      </li>
      <li className="flex items-center gap-1.5" data-id="element-112" data-mp="2cvjmqms9ci3p">
        <GlobeIcon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-113" data-mp="2cyflgdbxjat7" />
        Pay in your local currency
      </li>
      {offer.guaranteeDays && <li className="flex items-center gap-1.5" data-id="element-114" data-mp="b581urpaa71i">
          <ShieldCheckIcon aria-hidden="true" className="h-4 w-4 text-gold" data-id="element-115" data-mp="b2lyl49fedp3" />
          {offer.guaranteeDays}-day guarantee
        </li>}
    </ul>;
}
export function Hero() {
  const reduce = useReducedMotion();
  const enter = reduce ? {} : {
    initial: {
      opacity: 0,
      scale: 0.96
    },
    animate: {
      opacity: 1,
      scale: 1
    },
    transition: {
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }
  };
  return <header className="bg-onyx" data-id="element-116" data-mp="2ecyye87kbk11">
      {/* Mobile & tablet: title, mockup and button fit the first screen */}
      <div className="px-5 pb-12 lg:hidden" data-id="element-117" data-mp="2aavbmnfg0t0g">
        <div className="flex min-h-[100svh] flex-col items-center justify-center gap-[2.2svh] py-[3svh] text-center" data-id="element-118" data-mp="2adzn85v8mr15">
          <Pill data-id="element-119" data-mp="2jclcskz4n6bf" />
          <Headline className="text-[clamp(1.9rem,8.6vw,3.6rem)] sm:max-w-2xl" data-id="element-120" data-mp="2jc3bvb1is3x6" />
          <motion.div {...enter} className="flex w-full justify-center" data-id="element-121" data-mp="txn82pi0r54j">
            <HeroImage className="h-[min(44svh,92vw)] w-auto max-w-full" data-id="element-122" data-mp="tzrd5arnma6e" />
          </motion.div>
          <div className="w-full max-w-md" data-id="element-123" data-mp="p0oj0ld7pod7">
            <CtaButton href={OFFER_ANCHOR} fullWidth data-id="element-124" data-mp="qg92c7ex1vwf">
              Get HYROX Mega Pack now
            </CtaButton>
          </div>
          <TrustRow center data-id="element-125" data-mp="vdj2oi3ufrz1" />
        </div>
        <div className="mx-auto max-w-xl pt-2 text-center" data-id="element-126" data-mp="10dx8dthz2ptw">
          <Details center data-id="element-127" data-mp="10ege8buclnrk" />
        </div>
      </div>

      {/* Desktop: two columns within one screen height */}
      <div className="mx-auto hidden min-h-[100svh] max-w-6xl grid-cols-[1.08fr_1fr] items-center gap-12 px-5 py-10 lg:grid" data-id="element-128" data-mp="15c8fvxksoo8y">
        <div data-id="element-129" data-mp="cocewgo70a6d">
          <Pill data-id="element-130" data-mp="cnt55n8p4s3f" />
          <Headline className="mt-5 text-[clamp(3.25rem,5.4vw,4.6rem)]" data-id="element-131" data-mp="cpe0hf2u4f2c" />
          <div className="mt-5" data-id="element-132" data-mp="hnp9xop7wnk1">
            <Details data-id="element-133" data-mp="hmnm0jn3s6kw" />
          </div>
          <div className="mt-7" data-id="element-134" data-mp="hlkwog5fic73">
            <CtaButton href={OFFER_ANCHOR} data-id="element-135" data-mp="menu4zdbu5sk">Get HYROX Mega Pack now</CtaButton>
          </div>
          <div className="mt-5" data-id="element-136" data-mp="3l4e9uruaoz0q">
            <TrustRow data-id="element-137" data-mp="3l3cwuhtpy2gd" />
          </div>
        </div>
        <motion.div {...enter} className="flex flex-col items-center" data-id="element-138" data-mp="3q38ywbiuvjta">
          <HeroImage className="h-auto max-h-[72svh] w-full" data-id="element-139" data-mp="3q14m142zbagv" />
          <p className="-mt-2 border border-moss bg-forest px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-gold" data-id="element-140" data-mp="27737agitf5l9">
            Your next 26 weeks, fully mapped out
          </p>
        </motion.div>
      </div>
    </header>;
}